import type { Event } from "@viagogo/cx-sentry";

type ExceptionValue = NonNullable<NonNullable<Event["exception"]>["values"]>[number] & {
  stacktrace?: {
    frames?: Array<{
      abs_path?: string;
      filename?: string;
      module?: string;
    }>;
  };
};

type GoogleSignInEvent = Event & {
  exception?: {
    values?: ExceptionValue[];
  };
};

const GOOGLE_IDENTITY_SCRIPT_PATH = "/gsi/client";
const MISSING_REQUEST_TYPE_MESSAGE = "Missing request type.";
const PUBLIC_KEY_CREDENTIALS_MESSAGE = "The user agent does not support public key credentials";
const CREDENTIAL_MANAGEMENT_SERVICE_MESSAGE = "Error connecting to Credential Management service";
const NOT_SUPPORTED_ERROR_TYPE = "NotSupportedError";

const getExceptionValues = (event: GoogleSignInEvent): ExceptionValue[] => event.exception?.values ?? [];

const hasGoogleIdentityFrame = (exceptionValues: ExceptionValue[]): boolean =>
  exceptionValues.some((exceptionValue) =>
    exceptionValue.stacktrace?.frames?.some((frame) =>
      [frame.abs_path, frame.filename, frame.module].some((frameValue) =>
        frameValue?.includes(GOOGLE_IDENTITY_SCRIPT_PATH),
      ),
    ),
  );

const isMissingRequestTypeNotSupportedError = (exceptionValues: ExceptionValue[]): boolean =>
  exceptionValues.some(
    (exceptionValue) =>
      exceptionValue.type === NOT_SUPPORTED_ERROR_TYPE && exceptionValue.value === MISSING_REQUEST_TYPE_MESSAGE,
  );

const isPublicKeyCredentialsNotSupportedError = (exceptionValues: ExceptionValue[]): boolean =>
  exceptionValues.some(
    (exceptionValue) =>
      exceptionValue.type === NOT_SUPPORTED_ERROR_TYPE &&
      exceptionValue.value?.includes(PUBLIC_KEY_CREDENTIALS_MESSAGE),
  );

// No hasGoogleIdentityFrame guard — the rejection originates inside the browser's credential manager, not in JS, so no GSI frames exist.
const isCredentialManagementNotSupportedError = (exceptionValues: ExceptionValue[]): boolean =>
  exceptionValues.some(
    (exceptionValue) =>
      exceptionValue.type === NOT_SUPPORTED_ERROR_TYPE &&
      exceptionValue.value?.includes(CREDENTIAL_MANAGEMENT_SERVICE_MESSAGE),
  );

export const googleSignInEventProcessor = (event: Event): Event | null => {
  try {
    const googleSignInEvent = event as GoogleSignInEvent;
    const exceptionValues = getExceptionValues(googleSignInEvent);

    if (isMissingRequestTypeNotSupportedError(exceptionValues) && hasGoogleIdentityFrame(exceptionValues)) {
      return null;
    }

    if (isPublicKeyCredentialsNotSupportedError(exceptionValues) && hasGoogleIdentityFrame(exceptionValues)) {
      return null;
    }

    if (isCredentialManagementNotSupportedError(exceptionValues)) {
      return null;
    }

    return event;
  } catch {
    return event;
  }
};
